import type {ImsTrend} from "@/types";
import {useEffect, useRef} from "react";
import * as echarts from "echarts";
import {useAppStore} from "@/store/appStore.ts";
import {formatBatch, formatCompactNumber} from "@/utils/format.ts";

export const ImsChart = ({trend}: { trend: ImsTrend[] }) => {
    const ref = useRef<HTMLDivElement>(null);
    const chartRef = useRef<echarts.ECharts | null>(null);
    const selectExchange = useAppStore().selectExchange

    useEffect(() => {
        if (!ref.current) return;
        const chart = echarts.init(ref.current);
        chartRef.current = chart;
        const resize = () => chart.resize();
        const observer = new ResizeObserver(resize);
        observer.observe(ref.current);
        window.addEventListener('resize', resize);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', resize);
            chart.dispose();
        };
    }, []);

    const render_tool_tip =(params: any)=>{
        console.log(params)
        const color = params.seriesName === 'Total' ? '#dc2626' : params.seriesName === 'VBP' ? '#a39992' : '#c8c2be';
        const d = params.data || {};
        return `
                <div style="background:${color};color:#fff;padding:8px 10px;border-radius:6px;line-height:1.5">
                    <div style="font-weight:700;margin-bottom:2px">${params.seriesName}</div>
                    Month&nbsp;&nbsp;&nbsp;&nbsp;${formatBatch(params.name)}<br/>
                    Act&nbsp;$&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${formatCompactNumber(d.value,1,'K',selectExchange,false)}<br/>
                    Gro%&nbsp;&nbsp;&nbsp;&nbsp;${d.growthPct == null ? '--' : (Number(d.growthPct) * 100).toFixed(1) + '%'}<br/>
                    Vol Gro%&nbsp;${d.volGrowthPct == null ? '--' : (Number(d.volGrowthPct) * 100).toFixed(1) + '%'}
                </div>
                `
    }

    const render_chart = ()=>{
        const months = trend.map((t) => t.yearMonth);
        const toSeries = (key: string, gKey: string, vgKey: string) =>
            trend.map((t) => ({value: (t as any)[key], growthPct: (t as any)[gKey], volGrowthPct: (t as any)[vgKey]}));
        return {
            tooltip: {
                trigger: 'item',
                borderWidth: 0, padding: 0,
                formatter: render_tool_tip,
            },
            legend: {
                data: ['Total', 'VBP', 'Non-VBP'],
                left: 10,
                top: 0,
                icon: 'roundRect',
                itemWidth: 14,
                itemHeight: 3,
                textStyle: {fontSize: 10, color: '#64748b'}
            },
            grid: {left: 50, right: 20, top: 40, bottom: 28},
            xAxis: {
                type: 'category',
                data: months,
                boundaryGap: true,
                axisLabel: {fontSize: 9, color: '#94a3b8', formatter: formatBatch},
                axisLine: {lineStyle: {color: '#e2e8f0'}}
            },
            yAxis: {
                type: 'value',
                axisLabel: {fontSize: 9, color: '#94a3b8'},
                splitLine: {lineStyle: {color: '#f1f5f9'}}
            },
            series: [
                {
                    name: 'Total',
                    type: 'line',
                    data: toSeries('imsTotal', 'totalGrowthPct', 'totalVolGrowthPct'),
                    lineStyle: {color: '#dc2626', width: 2},
                    itemStyle: {color: '#dc2626'},
                    symbol: 'circle',
                    symbolSize: 5
                },
                {
                    name: 'VBP',
                    type: 'bar',
                    stack: 'ims-breakdown',
                    data: toSeries('imsVbp', 'vbpGrowthPct', 'vbpVolGrowthPct'),
                    lineStyle: {color: '#a39992', width: 1.5},
                    itemStyle: {color: '#a39992'},
                    symbol: 'circle',
                    symbolSize: 4
                },
                {
                    name: 'Non-VBP',
                    type: 'bar',
                    stack: 'ims-breakdown',
                    data: toSeries('imsNonVbp', 'nonVbpGrowthPct', 'nonVbpVolGrowthPct'),
                    lineStyle: {color: '#c8c2be', width: 1.5},
                    itemStyle: {color: '#c8c2be'},
                    symbol: 'circle',
                    symbolSize: 4
                },
            ],
        }
    }


    useEffect(() => {
        if (!chartRef.current) return;
        chartRef.current.setOption(render_chart());
    }, [trend]);

    return <div ref={ref} style={{width: '100%', height: '100%'}}/>;
}
